import React, { useState, useEffect } from "react";
import { formatDate, formatTime } from "../utils/formatDate";
import {
  getTimeSpentSeconds,
  formatTimeSpent,
  formatQuestionDuration,
} from "../utils/timeSpent";
import { Form, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FaRegCalendarCheck } from "react-icons/fa6";
import {
  FaRegPlayCircle,
  FaRegStopCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaRegCircle,
  FaClipboardList,
  FaBullseye,
  FaRegClock,
  FaPrint,
  FaChevronUp,
  FaUserGraduate,
} from "react-icons/fa";
import "../Styles/ExamResult.css";
import formatName from "../utils/formatName";

const brandBlue = "#263F86";
const borderColor = "#EEF0F4";
const softBlue = "rgba(95, 112, 245, 0.10)";
const successColor = "#034f22";
const dangerColor = "#931406";
const warningColor = "#7A4A00";
const mutedColor = "#3f3f41";

function ExamResult() {
  const [questions, setQuestions] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("assessmentId");
  const passedUserId = searchParams.get("userId");
  const attempt = searchParams.get("attempt");
  const username = searchParams.get("username");
  const created_at = searchParams.get("created_at");

  const [summary, setSummary] = useState({
    answered: 0,
    correct: 0,
    totalTime: null,
    totalQuestions: 0,
  });

  const [attemptTime, setAttemptTime] = useState({ start: null, end: null });
  const [reviewQuestion, setReviewQuestion] = useState(null);
  const [reviewStatus, setReviewStatus] = useState("all");
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    if (passedUserId) {
      setUserId(passedUserId);
    } else {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.user_id) {
            setUserId(parsedUser.user_id);
          }
        } catch (error) {
          console.error("Error parsing user from localStorage:", error);
        }
      }
    }
  }, [passedUserId]);

  useEffect(() => {
    const fetchTotalQuestions = async () => {
      if (!assessmentId) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/getTotalQuestions?assessment_id=${assessmentId}`
        );
        const data = await response.json();
        if (data.success) {
          setSummary((prev) => ({
            ...prev,
            totalQuestions: data.totalQuestions,
          }));
        } else {
          console.error("Error fetching total questions:", data.message);
        }
      } catch (error) {
        console.error("Error fetching total questions:", error);
      }
    };

    fetchTotalQuestions();
  }, [assessmentId]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!userId || !assessmentId || !attempt) return;

      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API}/getAttempt?user_id=${userId}&assessment_id=${assessmentId}&attempt=${attempt}`
        );
        const data = await response.json();
        if (data.success) {
          const formattedQuestions = data.data.map((question) => ({
            ...question,
            options: question.answers.map((answer) => ({
              answer_id: answer.answer_id,
              text: answer.answer_text,
              is_correct: parseInt(answer.is_correct, 10),
              choice_status: answer.choice_status,
              answer_status: answer.answer_status,
              score: answer.score || 0,
            })),
          }));

          const totalAnswered = formattedQuestions.filter((q) =>
            q.options.some((a) => a.choice_status === "Chosen")
          ).length;
          const totalCorrect = formattedQuestions.filter((q) =>
            q.options.some(
              (a) => a.is_correct === 1 && a.choice_status === "Chosen"
            )
          ).length;
          const totalTimeSpent = getTimeSpentSeconds(formattedQuestions);

          setQuestions(formattedQuestions);
          setAttemptTime(data.attempt_time || { start: null, end: null });
          setSummary((prev) => ({
            ...prev,
            answered: totalAnswered,
            correct: totalCorrect,
            totalTime: formatTimeSpent(totalTimeSpent),
          }));
        } else {
          console.error("Error fetching questions:", data.message);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [userId, assessmentId, attempt]);

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReviewChange = (e) => {
    if (e.target.value === "") {
      setReviewQuestion(null);
      return;
    }
    const selectedQuestion = parseInt(e.target.value, 10);
    if (!isNaN(selectedQuestion)) {
      setReviewQuestion(selectedQuestion - 1);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const totalQuestionsCount = summary.totalQuestions || 0;
  const percentage =
    totalQuestionsCount > 0
      ? Math.round((summary.correct / totalQuestionsCount) * 100)
      : 0;
  const percentageColor =
    percentage >= 70 ? successColor : percentage >= 40 ? warningColor : dangerColor;

  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const scoreOffset = circumference - (percentage / 100) * circumference;

  // Start and end are Singapore time, worked out by the server.
  const formattedDate = attemptTime.start
    ? formatDate(attemptTime.start, "--")
    : formatDate(created_at, "--");
  const startTimeText = formatTime(attemptTime.start, "--");
  const endTimeText = formatTime(attemptTime.end, "--");
  const endsOnAnotherDay =
    attemptTime.start &&
    attemptTime.end &&
    formatDate(attemptTime.end) !== formatDate(attemptTime.start);

  const questionMatchesStatus = (question) => {
    const chosen = question.options.find((option) => option.choice_status === "Chosen");
    if (reviewStatus === "unanswered") return !chosen;
    if (reviewStatus === "correct") return chosen?.is_correct === 1;
    if (reviewStatus === "incorrect") return Boolean(chosen) && chosen.is_correct !== 1;
    return true;
  };

  const questionsToRender = reviewQuestion !== null
    ? [questions[reviewQuestion]].filter(Boolean)
    : questions.filter(questionMatchesStatus);

  return (
    <div
      className="container-fluid p-4 exam-result-page"
      style={{
        fontFamily: "Manrope-Regular",
        background: "#F4F6FB",
        minHeight: "100vh",
      }}
    >
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
        .exam-result-select:focus, .exam-result-select {
          outline: none;
          box-shadow: none;
        }
      `}</style>

      <div className="exam-result-shell">
        {loading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: "80vh" }}
          >
            <Spinner animation="border" variant="primary" />
            <p className="mt-3" style={{ fontFamily: "Manrope-Regular" }}>
              Please Wait...
            </p>
          </div>
        ) : (
          <>
            {/* Page Header */}
            <header className="d-flex justify-content-between align-items-center mb-3 no-print exam-result-header">
              <div>
                <p className="exam-result-eyebrow">PERFORMANCE REVIEW</p>
                <h1>Assessment result</h1>
                <p className="exam-result-intro">
                  Detailed performance breakdown for this attempt
                </p>
              </div>
              <button
                onClick={handlePrint}
                className="exam-result-print-button"
                style={{
                  background: "#F8FAFC",
                  color: brandBlue,
                  border: `1px solid ${borderColor}`,
                  borderRadius: "9px",
                  padding: "9px 16px",
                  fontWeight: 700,
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaPrint size={13} />
                Print
              </button>
            </header>

            {/* Summary Card */}
            <section className="exam-result-summary"
              style={{
                background: "#fff",
                borderRadius: "16px",
                border: `1px solid ${borderColor}`,
                boxShadow: "0 2px 10px rgba(38, 63, 134, 0.05)",
                padding: "28px",
                marginBottom: "24px",
              }}
            >
              <div className="d-flex flex-wrap justify-content-between align-items-center exam-result-summary__top">
                <div className="d-flex align-items-center mb-3 mb-md-0 exam-result-candidate">
                  <div className="exam-result-candidate__avatar"
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "50%",
                      background: softBlue,
                      color: brandBlue,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "22px",
                      marginRight: "16px",
                      flexShrink: 0,
                    }}
                  >
                    <FaUserGraduate />
                  </div>
                  <div>
                    <h2>
                      {formatName(username) || "Student"}
                    </h2>
                    <div
                      className="d-flex flex-wrap align-items-center"
                      style={{ color: mutedColor, fontSize: "14px", gap: "14px" }}
                    >
                      <span className="d-flex align-items-center">
                        <FaRegCalendarCheck size={14} className="me-2" />
                        {formattedDate}
                      </span>
                      <span className="d-flex align-items-center">
                        <FaRegPlayCircle size={14} className="me-2" />
                        Start time: {startTimeText}
                      </span>
                      <span className="d-flex align-items-center">
                        <FaRegStopCircle size={14} className="me-2" />
                        End time:{" "}
                        {endsOnAnotherDay
                          ? `${formatDate(attemptTime.end)}, ${endTimeText}`
                          : endTimeText}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Score Ring */}
                <div className="d-flex align-items-center exam-result-score-ring">
                  <svg width="110" height="110" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke={borderColor}
                      strokeWidth="10"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke={percentageColor}
                      strokeWidth="10"
                      strokeDasharray={circumference}
                      strokeDashoffset={scoreOffset}
                      strokeLinecap="round"
                      transform="rotate(-90 60 60)"
                      style={{ transition: "stroke-dashoffset 0.6s ease" }}
                    />
                    <text
                      x="60"
                      y="57"
                      textAnchor="middle"
                      fontSize="24"
                      fontWeight="800"
                      fill="#1F2937"
                    >
                      {percentage}%
                    </text>
                    <text
                      x="60"
                      y="76"
                      textAnchor="middle"
                      fontSize="11"
                      fill={mutedColor}
                    >
                      Score
                    </text>
                  </svg>
                </div>
              </div>

              {/* Stat tiles */}
              <div className="row g-3 mt-1">
                {[
                  {
                    label: "Total Questions",
                    value: summary.totalQuestions || 0,
                    icon: <FaClipboardList size={16} />,
                  },
                  {
                    label: "Answered",
                    value: `${summary.answered}/${summary.totalQuestions || 0}`,
                    icon: <FaCheckCircle size={16} />,
                  },
                  {
                    label: "Correct Answers",
                    value: summary.correct,
                    icon: <FaBullseye size={16} />,
                  },
                  {
                    label: "Total Time",
                    value: summary.totalTime,
                    icon: <FaRegClock size={16} />,
                  },
                ]
                  .filter((stat) => stat.value !== null)
                  .map((stat, idx) => (
                  <div className="col-6 col-md-3" key={idx}>
                    <div className="exam-result-stat"
                      style={{
                        background: "#F8FAFC",
                        border: `1px solid ${borderColor}`,
                        borderRadius: "12px",
                        padding: "14px",
                        height: "100%",
                      }}
                    >
                      <div
                        style={{ color: brandBlue, marginBottom: "6px" }}
                      >
                        {stat.icon}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: mutedColor,
                          marginBottom: "2px",
                        }}
                      >
                        {stat.label}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 800,
                          color: "#1F2937",
                        }}
                      >
                        {stat.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Review toolbar */}
            <section
              className="d-flex justify-content-between align-items-center mb-3 no-print exam-result-toolbar"
              style={{ gap: "12px" }}
            >
              <div>
                <h2>Question review</h2>
                <p>Review answers, scores, and time spent per question.</p>
              </div>
              <div className="d-flex align-items-center exam-result-toolbar__controls">
                <Form.Select
                  aria-label="Filter questions by result"
                  value={reviewStatus}
                  onChange={(event) => {
                    setReviewStatus(event.target.value);
                    setReviewQuestion(null);
                  }}
                >
                  <option value="all">All results</option>
                  <option value="correct">Correct</option>
                  <option value="incorrect">Incorrect</option>
                  <option value="unanswered">Not answered</option>
                </Form.Select>
                <select
                  className="form-select exam-result-select"
                  style={{
                    width: "170px",
                    borderRadius: "9px",
                    border: `1px solid ${borderColor}`,
                    fontSize: "14px",
                  }}
                  onChange={handleReviewChange}
                  value={reviewQuestion !== null ? reviewQuestion + 1 : ""}
                >
                  <option value="">Review a question</option>
                  {questions.map((_, index) => (
                    <option key={index} value={index + 1}>
                      Question {index + 1}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {/* Question Cards */}
            {questionsToRender.length === 0 ? (
              <div className="exam-result-empty"
                style={{
                  background: "#fff",
                  border: `1px solid ${borderColor}`,
                  borderRadius: "14px",
                  padding: "40px",
                  textAlign: "center",
                  color: mutedColor,
                }}
              >
                <strong>No matching questions</strong>
                <span>Try choosing another review filter.</span>
              </div>
            ) : (
              questionsToRender.map((question, questionIndex) => {
                if (!question) return null;

                const displayIndex =
                  reviewQuestion !== null ? reviewQuestion + 1 : questionIndex + 1;
                const chosenOption = question.options.find(
                  (option) => option.choice_status === "Chosen"
                );
                const isAnswered = Boolean(chosenOption);
                const isCorrect = chosenOption?.is_correct === 1;
                const questionScore = chosenOption?.score || 0;

                const statusLabel = !isAnswered
                  ? "Not Answered"
                  : isCorrect
                  ? "Correct"
                  : "Incorrect";
                const statusColor = !isAnswered
                  ? mutedColor
                  : isCorrect
                  ? successColor
                  : dangerColor;

                return (
                  <article className="exam-result-question"
                    key={questionIndex}
                    style={{
                      background: "#fff",
                      border: `1px solid ${borderColor}`,
                      borderRadius: "14px",
                      boxShadow: "0 2px 8px rgba(38, 63, 134, 0.04)",
                      padding: "22px",
                      marginBottom: "18px",
                    }}
                  >
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <span className="exam-result-question__number"
                          style={{
                            background: softBlue,
                            color: brandBlue,
                            fontWeight: 800,
                            fontSize: "13px",
                            borderRadius: "8px",
                            padding: "4px 10px",
                          }}
                        >
                          Question {displayIndex} of {questions.length}
                        </span>
                        <span className={`exam-result-question__status exam-result-question__status--${!isAnswered ? "unanswered" : isCorrect ? "correct" : "incorrect"}`}
                          style={{
                            background: `${statusColor}1A`,
                            color: statusColor,
                            fontWeight: 700,
                            fontSize: "12px",
                            borderRadius: "8px",
                            padding: "4px 10px",
                          }}
                        >
                          {statusLabel}
                        </span>
                      </div>
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "16px", fontSize: "13px", color: mutedColor }}
                      >
                        <span className="d-flex align-items-center">
                          <FaBullseye size={12} className="me-1" />
                          Score: <strong style={{ color: "#1F2937", marginLeft: "4px" }}>{questionScore}</strong>
                        </span>
                        {formatQuestionDuration(question) && (
                          <span className="d-flex align-items-center">
                            <FaRegClock size={12} className="me-1" />
                            {formatQuestionDuration(question)}
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="exam-result-question__title">
                      {question.question_text}
                    </h3>

                    <div>
                      {question.options.map((option, index) => {
                        const isChosen = option.choice_status === "Chosen";
                        const isRight = option.is_correct === 1;

                        let icon = <FaRegCircle size={16} color={mutedColor} />;
                        let borderClr = borderColor;
                        let bgClr = "#FAFBFC";

                        if (isChosen && isRight) {
                          icon = <FaCheckCircle size={16} color={successColor} />;
                          borderClr = "#B7E4C7";
                          bgClr = "#EAFBF0";
                        } else if (isChosen && !isRight) {
                          icon = <FaTimesCircle size={16} color={dangerColor} />;
                          borderClr = "#F5B7B1";
                          bgClr = "#FDECEA";
                        } else if (!isChosen && isRight) {
                          icon = <FaCheckCircle size={16} color={successColor} />;
                          borderClr = "#D7F2E0";
                          bgClr = "#F3FBF6";
                        }

                        return (
                          <div className={`d-flex flex-wrap justify-content-between align-items-center exam-result-option ${isChosen && isRight ? "exam-result-option--selected-correct" : isChosen && !isRight ? "exam-result-option--selected-wrong" : !isChosen && isRight ? "exam-result-option--correct" : ""}`}
                            key={index}
                            style={{
                              border: `1px solid ${borderClr}`,
                              background: bgClr,
                              borderRadius: "10px",
                              padding: "10px 14px",
                              marginBottom: "8px",
                              gap: "8px",
                            }}
                          >
                            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                              {icon}
                              <span style={{ fontSize: "14px", color: "#1F2937" }}>
                                {option.text}
                              </span>
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                              {isChosen && (
                                <span className="exam-result-option__selected"
                                  style={{
                                    background: brandBlue,
                                    color: "#fff",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    borderRadius: "6px",
                                    padding: "3px 8px",
                                  }}
                                >
                                  Your Answer
                                </span>
                              )}
                              
                              {/* {isRight && (
                                <span
                                  style={{
                                    background: successColor,
                                    color: "#fff",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    borderRadius: "6px",
                                    padding: "3px 8px",
                                  }}
                                >
                                  Correct Answer
                                </span>
                              )} */}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </article>
                );
              })
            )}

            {showScrollButton && (
              <button
                onClick={handleScrollToTop}
                className="no-print"
                data-role="scroll-top"
                style={{
                  position: "fixed",
                  bottom: "24px",
                  right: "24px",
                  width: "48px",
                  height: "48px",
                  backgroundColor: brandBlue,
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  boxShadow: "0 4px 12px rgba(38, 63, 134, 0.35)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Scroll to top"
              >
                <FaChevronUp size={16} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ExamResult;
